import type { TimeframeKey, UTBotConfig, Token } from '../types/index.js';

// ─── API Endpoints ───────────────────────────────────────────────────────────

export const API_URLS = {
  coingecko: {
    base: 'https://api.coingecko.com/api/v3',
    pro: 'https://pro-api.coingecko.com/api/v3',
  },
  birdeye: {
    base: 'https://public-api.birdeye.so',
  },
  binance: {
    rest: 'https://api.binance.com/api/v3',
    ws: 'wss://stream.binance.com:9443/ws',
  },
  cryptoPanic: {
    base: 'https://cryptopanic.com/api/v1',
  },
  hyperliquid: {
    rest: 'https://api.hyperliquid.xyz',
    ws:   'wss://api.hyperliquid.xyz/ws',
    info:     'https://api.hyperliquid.xyz/info',
    exchange: 'https://api.hyperliquid.xyz/exchange',
  },
  jupiter: {
    quote:  'https://quote-api.jup.ag/v6/quote',
    swap:   'https://quote-api.jup.ag/v6/swap',
  },
  solana: {
    rpc: 'https://api.mainnet-beta.solana.com',
  },
} as const;

// Hyperliquid well-known token mints (USDC on Solana)
export const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

// ─── Timeframe Config ────────────────────────────────────────────────────────

export const TIMEFRAME_MS: Record<TimeframeKey, number> = {
  '1m':  60_000,
  '5m':  300_000,
  '15m': 900_000,
  '30m': 1_800_000,
  '1h':  3_600_000,
  '4h':  14_400_000,
  '1D':  86_400_000,
  '1W':  604_800_000,
};

// Binance kline interval mapping
export const TIMEFRAME_TO_BINANCE: Record<TimeframeKey, string> = {
  '1m':  '1m',
  '5m':  '5m',
  '15m': '15m',
  '30m': '30m',
  '1h':  '1h',
  '4h':  '4h',
  '1D':  '1d',
  '1W':  '1w',
};

// CoinGecko days param mapping (for OHLC endpoint)
export const TIMEFRAME_TO_CG_DAYS: Record<TimeframeKey, number> = {
  '1m':  1,
  '5m':  1,
  '15m': 1,
  '30m': 7,
  '1h':  7,
  '4h':  30,
  '1D':  90,
  '1W':  365,
};

// ─── Default UT Bot Config ────────────────────────────────────────────────────

export const DEFAULT_UTBOT_CONFIG: UTBotConfig = {
  keyValue: 1,
  atrPeriod: 10,
  timeframe: '15m',
};

// ─── Default Watchlist Tokens ─────────────────────────────────────────────────

export const DEFAULT_TOKENS: Token[] = [
  {
    symbol: 'BTC',
    name: 'Bitcoin',
    decimals: 8,
    coingeckoId: 'bitcoin',
  },
  {
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    coingeckoId: 'ethereum',
  },
  {
    symbol: 'SOL',
    name: 'Solana',
    decimals: 9,
    coingeckoId: 'solana',
    chain: 'solana',
    address: 'So11111111111111111111111111111111111111112',
    birdeyeId: 'So11111111111111111111111111111111111111112',
  },
  {
    symbol: 'BNB',
    name: 'BNB',
    decimals: 18,
    coingeckoId: 'binancecoin',
    chain: 'bsc',
  },
  {
    symbol: 'WIF',
    name: 'dogwifhat',
    decimals: 6,
    coingeckoId: 'dogwifcoin',
    chain: 'solana',
    address: 'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm',
    birdeyeId: 'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm',
  },
  {
    symbol: 'JTO',
    name: 'Jito',
    decimals: 9,
    coingeckoId: 'jito-governance-token',
    chain: 'solana',
    address: 'jtojtomepa8beP8AuQc6eXt5FriJwfFMwQx2v2f9mCL',
    birdeyeId: 'jtojtomepa8beP8AuQc6eXt5FriJwfFMwQx2v2f9mCL',
  },
];

// ─── Chart Defaults ───────────────────────────────────────────────────────────

export const CHART_CANDLE_LIMIT = 500;  // Max candles to fetch/store per panel
export const SIGNAL_HISTORY_LIMIT = 100; // Max signals to keep in feed

// ─── Theme CSS Variables ──────────────────────────────────────────────────────

export const THEME_VARS = {
  arc: {
    '--color-arc':    '#00BFFF',
    '--color-accent': '#FFD700',
    '--color-buy':    '#00FF88',
    '--color-sell':   '#FF3B3B',
    '--color-panel':  '#050e1e',
    '--color-border': '#1a3a5c',
    '--color-text':   '#7EC8E3',
    '--color-bright': '#e0f4ff',
    '--color-bg':     '#020a14',
  },
  gold: {
    '--color-arc':    '#FFD700',
    '--color-accent': '#FFA500',
    '--color-buy':    '#ADFF2F',
    '--color-sell':   '#FF4500',
    '--color-panel':  '#0e0a00',
    '--color-border': '#3a2a00',
    '--color-text':   '#CCAA44',
    '--color-bright': '#FFF8DC',
    '--color-bg':     '#060400',
  },
  matrix: {
    '--color-arc':    '#00FF88',
    '--color-accent': '#AAFF00',
    '--color-buy':    '#33FF33',
    '--color-sell':   '#FF3333',
    '--color-panel':  '#010e06',
    '--color-border': '#004020',
    '--color-text':   '#44AA66',
    '--color-bright': '#AAFFCC',
    '--color-bg':     '#000a03',
  },
  crimson: {
    '--color-arc':    '#DC143C',
    '--color-accent': '#FF6060',
    '--color-buy':    '#FF4444',
    '--color-sell':   '#8B0000',
    '--color-panel':  '#0e0005',
    '--color-border': '#3a0010',
    '--color-text':   '#C06070',
    '--color-bright': '#FFB0B8',
    '--color-bg':     '#060002',
  },
} as const;
